import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  Paper,
  Grid,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Drawer,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import apiService from '../../services/apiService';
import { format } from 'date-fns';
import AddCustomerDialog from './AddCustomerDialog';
import AddProductDialog from './AddProductDialog';

const filter = createFilterOptions();

const ProductBill = () => {
  const [productSearch, setProductSearch] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerInput, setCustomerInput] = useState('');
  const customerInputRef = useRef(null);
  const salesPriceInputRef = useRef(null);
  const quantityInputRef = useRef(null);

  const [salesList, setSalesList] = useState([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);

  const [form, setForm] = useState({
    salesDetailId: undefined,
    customer: null,
    actualPrice: '',
    salesPrice: '',
    quantity: '',
    price: '',
  });
  const [saving, setSaving] = useState(false);
  const [productsSoldToday, setProductsSoldToday] = useState([]);

  // State for Add Customer Dialog
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);
  // State for Add Product Dialog
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.products.list({
        page: 0,
        size: 1000,
        sort: 'productName,asc',
      });
      setAllProducts(Array.isArray(response?.data?.content) ? response.data.content : []);
    } catch (error) {
      console.error('Error loading products:', error);
      setAllProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCustomers = useCallback(async () => {
    try {
      const resp = await apiService.customers.getMinDetails();
      setCustomers(Array.isArray(resp.data) ? resp.data : []);
    } catch (e) {
      setCustomers([]);
    }
  }, []);

  const loadProductsSoldToday = useCallback(async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const resp = await apiService.salesDetails.productsSoldToday(today);
      setProductsSoldToday(Array.isArray(resp.data) ? resp.data : []);
    } catch (error) {
      setProductsSoldToday([]);
    }
  }, []);

  useEffect(() => {
    loadProducts();
    loadCustomers();
    loadProductsSoldToday();
  }, [loadProducts, loadCustomers, loadProductsSoldToday]);

  const fetchSalesByProduct = useCallback(async (product, p = page, s = size) => {
    if (!product?.productId) {
      setSalesList([]);
      setTotalPages(0);
      return;
    }
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const resp = await apiService.salesDetails.listByProductAndDate({
        productId: product.productId,
        date: today,
        page: p,
        size: s,
        sortBy: 'updateTimestamp',
        direction: 'DESC',
      });
      const data = resp.data;
      const content = data?.content ?? [];
      setSalesList(content);
      setTotalPages(data?.totalPages ?? 0);
    } catch (e) {
      setSalesList([]);
      setTotalPages(0);
    }
  }, [page, size]);

  useEffect(() => {
    if (selectedProduct) {
      fetchSalesByProduct(selectedProduct, page, size);
    } else {
      setSalesList([]);
    }
  }, [selectedProduct, page, size, fetchSalesByProduct]);

  const onProductChange = (_e, val) => {
    if (val?.isAddNew) {
        setAddProductDialogOpen(true);
        return;
    }
    setSelectedProduct(val || null);
    setForm((prev) => ({
      ...prev,
      actualPrice: val?.actualPrice ?? '',
      salesPrice: val?.sellingPrice ?? '',
    }));
    if (val) {
      setTimeout(() => {
        if (customerInputRef.current) {
          customerInputRef.current.focus();
        }
      }, 100);
    }
  };

  const handleSaveNewProduct = async (newProduct) => {
    await loadProducts();
    setSelectedProduct(newProduct);
    setForm((prev) => ({
        ...prev,
        actualPrice: newProduct.actualPrice || '',
        salesPrice: newProduct.sellingPrice || '',
    }));
    setAddProductDialogOpen(false);
    requestAnimationFrame(() => {
        customerInputRef.current?.focus();
    });
  };

  const onCustomerChange = (_e, val) => {
    if (val?.isAddNew) {
      setAddCustomerDialogOpen(true);
      return;
    }
    setSelectedCustomer(val || null);
    if (val) {
      setTimeout(() => {
        if (salesPriceInputRef.current) {
          salesPriceInputRef.current.focus();
        }
      }, 100);
    }
  };

  const handleSaveNewCustomer = async (newCustomer) => {
    // Refresh customer list
    await loadCustomers();

    // Select the new customer
    setSelectedCustomer(newCustomer);
    setCustomerInput(''); // Clear input value so the selected customer displays correctly

    setAddCustomerDialogOpen(false);

    // Focus on Sales Price input
    requestAnimationFrame(() => {
        salesPriceInputRef.current?.focus();
    });
  };

  const validate = () => {
    if (!selectedProduct?.productId) {
      alert('Select a product');
      return false;
    }
    if (!selectedCustomer?.customerId) {
      alert('Select a customer');
      return false;
    }
    if (Number(form.price || 0) <= 0) {
      alert('Price must be greater than 0.');
      return false;
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;

    try {
      setSaving(true);
      const payload = {
        productId: selectedProduct.productId,
        customerId: selectedCustomer.customerId,
        quantity: form.quantity,
        salesPrice: form.salesPrice !== '' ? form.salesPrice : selectedProduct?.actualPrice,
        price: form.price,
      };

      const response = await apiService.productBill.addItem(payload);

      const updatedSalesList = response.data.content || response.data;
      setSalesList(Array.isArray(updatedSalesList) ? updatedSalesList : []);

      await loadProductsSoldToday();
      setPage(0);

      setForm({
        salesDetailId: undefined,
        customer: null,
        actualPrice: selectedProduct?.actualPrice ?? '',
        salesPrice: selectedProduct?.sellingPrice ?? '',
        quantity: '',
        price: '',
      });
      setSelectedCustomer(null);
      setCustomerInput('');
      if (customerInputRef.current) {
        customerInputRef.current.focus();
      }
    } catch (e) {
      console.error('Failed to save:', e);
      alert('Failed to save item.');
    } finally {
      setSaving(false);
    }
  };

  const editRow = (row) => {
    const cust = customers.find((c) => c.customerId === row.customerId) || null;
    setSelectedCustomer(cust);
    setCustomerInput(cust ? `${cust.customerId} - ${cust.customerName} - ${cust.customerNameTamil}` : '');
    setForm({
      salesDetailId: row.salesDetailId,
      customer: cust,
      actualPrice: selectedProduct?.actualPrice ?? '',
      salesPrice: String(row.salesPrice ?? ''),
      quantity: String(row.quantity ?? ''),
      price: String(row.price ?? ''),
    });
  };

  const deleteRow = async (row) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      // Backend now handles bill summary recalculation and cleanup
      await apiService.salesDetails.delete(row.salesDetailId);

      // Refresh UI
      await fetchSalesByProduct(selectedProduct, 0, size);
      await loadProductsSoldToday();
      setPage(0);
    } catch (error) {
      console.error('Failed to delete item:', error);
      alert('Failed to delete item');
    }
  };

  const onPickSoldProduct = (item) => {
    const found = allProducts.find((p) => p.productId === item.productId);
    if (found) {
      setSelectedProduct(found);
      setForm({
        salesDetailId: undefined,
        customer: null,
        actualPrice: found?.actualPrice ?? '',
        salesPrice: found?.sellingPrice ?? '',
        quantity: '',
        price: '',
      });
      setSelectedCustomer(null);
      setCustomerInput('');
    }
  };

  const handleEnterToSave = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (parseFloat(form.price) > 0) {
        save();
      }
    }
  };

  const renderProductsSoldList = (isMobile = false) => (
    <Paper sx={{ p: 0.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, textAlign: 'center', flex: 1 }}>
          Products Sold Today
        </Typography>
        {isMobile && (
          <IconButton onClick={() => setMobileOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      <List dense sx={{ flex: 1, overflow: 'auto' }}>
        {productsSoldToday.map((item) => (
          <React.Fragment key={item.productId}>
            <ListItem button onClick={() => { onPickSoldProduct(item); if (isMobile) setMobileOpen(false); }}>
              <ListItemText
                primary={item.productNameTamil || item.productName}
              />
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        ))}
        {productsSoldToday.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>
            No products sold today.
          </Typography>
        )}
      </List>
    </Paper>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 24px)', p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5">Product Bill</Typography>
        {productsSoldToday.length > 0 && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { xs: 'flex', md: 'none' } }}
          >
            Sold Today
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 1.5, mb: 1 }}>
        <Autocomplete
          autoHighlight
          selectOnFocus
          handleHomeEndKeys
          options={allProducts}
          getOptionLabel={(o) => (o ? `${o.productId} - ${o.productName} - ${o.productNameTamil || ''}` : '')}
          isOptionEqualToValue={(option, value) => option && value && option.productId === value.productId}
          value={selectedProduct}
          onChange={onProductChange}
          inputValue={productSearch}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            const { inputValue } = params;
            const isExisting = options.some((option) => inputValue === option.productName);
            if (inputValue !== '' && !isExisting) {
                filtered.push({
                    productId: 'ADD_NEW', productName: `Add "${inputValue}"`, isAddNew: true
                });
            }
            return filtered;
          }}
          onInputChange={(event, newInputValue) => setProductSearch(newInputValue)}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Product"
              variant="outlined"
              placeholder="Type to search by Name, Code, or ID..."
            />
          )}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;
            if (option.isAddNew) {
                return (
                    <li key={key} {...otherProps} style={{ borderTop: '1px solid #eee', color: '#1976d2', fontWeight: 600 }}>
                        + {option.productName}
                    </li>
                );
            }
            return (
                <li key={key} {...otherProps}>
                    {`${option.productId} - ${option.productName} - ${option.productNameTamil || ''}`}
                </li>
            );
          }}
        />
      </Paper>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, flexGrow: 1 }}>
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Paper sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
              Add Customer Entry
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 1.5 } }}>
              <Box sx={{ flex: { xs: '1 1 calc(100% - 100px)', md: '2 1 250px' } }}> {/* Customer Autocomplete */}
                <Autocomplete
                  options={customers || []}
                  getOptionLabel={(o) => (o ? `${o.customerId} - ${o.customerName} - ${o.customerNameTamil} - ${o.customerCity}` : '')}
                  value={selectedCustomer}
                  onChange={onCustomerChange}
                  inputValue={customerInput}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);
                    const { inputValue } = params;
                    const isExisting = options.some((option) => inputValue === option.customerName);
                    if (inputValue !== '' && !isExisting) {
                        filtered.push({
                            customerId: 'ADD_NEW', customerName: `Add "${inputValue}"`, customerNameTamil: '', customerCity: '', isAddNew: true
                        });
                    }
                    return filtered;
                  }}
                  onInputChange={(_e, v) => setCustomerInput(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Customer"
                      size="small"
                      fullWidth
                      inputRef={customerInputRef}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const labelFor = (opt) => (opt ? `${opt.customerId} - ${opt.customerName} - ${opt.customerNameTamil}` : '');
                          const firstMatch = (customers || []).find(opt =>
                            labelFor(opt).toLowerCase().includes((customerInput || '').toLowerCase())
                          );
                          if (firstMatch) {
                            onCustomerChange(null, firstMatch);
                          }
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    if (option.isAddNew) {
                        return (
                            <li key={key} {...otherProps} style={{ borderTop: '1px solid #eee', color: '#1976d2', fontWeight: 600 }}>
                                + {option.customerName}
                            </li>
                        );
                    }
                    return (
                        <li key={key} {...otherProps}>
                            {`${option.customerId} - ${option.customerName} - ${option.customerNameTamil} - ${option.customerCity}`}
                        </li>
                    );
                  }}
                  isOptionEqualToValue={(o, v) => o.customerId === v?.customerId}
                />
              </Box>
              <Box sx={{ flex: { xs: '0 0 90px', sm: '1 1 100px' } }}>
                <TextField
                  label="Actual Price"
                  size="small"
                  value={form.actualPrice}
                  InputProps={{ readOnly: true }}
                  inputProps={{ tabIndex: -1 }}
                  fullWidth
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 85px', sm: '1 1 100px' } }}>
                <TextField
                  label="Sales Price"
                  inputRef={salesPriceInputRef}
                  size="small"
                  value={form.salesPrice}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm((prev) => {
                      const qtyNum = Number(prev.quantity || 0);
                      const unit = val === '' ? (Number(prev.actualPrice || 0) || 0) : (Number(val) || 0);
                      const computed = qtyNum > 0 ? String(unit * qtyNum) : '';
                      return { ...prev, salesPrice: val, price: computed };
                    });
                  }}
                  type="number"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (quantityInputRef.current) {
                        quantityInputRef.current.focus();
                      }
                    }
                  }}
                  fullWidth
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 85px', sm: '1 1 100px' } }}>
                <TextField
                  label="KG / Count"
                  inputRef={quantityInputRef}
                  size="small"
                  value={form.quantity}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm((prev) => {
                      const unit = prev.salesPrice !== '' ? (Number(prev.salesPrice) || 0) : (Number(prev.actualPrice || 0) || 0);
                      const computed = val === '' ? '' : String(unit * (Number(val) || 0));
                      return { ...prev, quantity: val, price: computed };
                    });
                  }}
                  onKeyDown={handleEnterToSave}
                  type="number"
                  fullWidth
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 85px', sm: '1 1 100px' } }}>
                <TextField
                  label="Price"
                  size="small"
                  value={form.price}
                  InputProps={{ readOnly: true }}
                  inputProps={{ tabIndex: -1 }}
                  onKeyDown={handleEnterToSave}
                  type="number"
                  fullWidth
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 auto', sm: '0 0 100px' }, display: 'flex', alignItems: 'center' }}>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving} fullWidth onKeyDown={handleEnterToSave}>
                  Save
                </Button>
              </Box>
            </Box>
          </Paper>
          {isMobile ? (
            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, pb: 1 }}>
              {(salesList || []).map((row, index) => {
                const cust = customers.find((c) => c.customerId === row.customerId);
                const customerName = cust?.customerNameTamil || cust?.customerName || '';
                return (
                  <Paper key={row.salesDetailId} sx={{ p: 0.75, mb: 0.5, mx: 0.5, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                        {index + 1}. {customerName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }}>
                        {row.salesPrice || selectedProduct?.actualPrice || 'N/A'} x {row.quantity} = <b>{row.price}</b>
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexShrink: 0, ml: 0.5 }}>
                      <IconButton size="small" onClick={() => editRow(row)} sx={{ p: 0.5 }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteRow(row)} sx={{ p: 0.5 }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                );
              })}
              {(salesList || []).length === 0 && (
                <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 4 }}>
                  No items added yet.
                </Typography>
              )}
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>S NO</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell align="right">Sales Price</TableCell>
                    <TableCell align="right">KG/Count</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(salesList || []).map((row, index) => {
                    const cust = customers.find((c) => c.customerId === row.customerId);
                    const customerName = cust?.customerNameTamil || cust?.customerName || '';
                    return (
                      <TableRow key={row.salesDetailId} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{customerName}</TableCell>
                        <TableCell align="right">{row.salesPrice || selectedProduct?.actualPrice || 'N/A'}</TableCell>
                        <TableCell align="right">{row.quantity}</TableCell>
                        <TableCell align="right">{row.price}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => editRow(row)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => deleteRow(row)}>
                            <CloseIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
        {productsSoldToday.length > 0 && (
          <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
            {renderProductsSoldList(false)}
          </Box>
        )}

        <Drawer
          anchor="right"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{ '& .MuiDrawer-paper': { width: '80%', maxWidth: 300 } }}
        >
          {renderProductsSoldList(true)}
        </Drawer>
      </Box>

      <AddCustomerDialog
        open={addCustomerDialogOpen}
        onClose={() => setAddCustomerDialogOpen(false)}
        onSaveSuccess={handleSaveNewCustomer}
      />
      <AddProductDialog
        open={addProductDialogOpen}
        onClose={() => setAddProductDialogOpen(false)}
        onSaveSuccess={handleSaveNewProduct}
      />
    </Box>
  );
};

export default ProductBill;
